import { sendbirdSelectors } from "@sendbird/uikit-react";
import type { GroupChannel } from "@sendbird/chat/groupChannel";
import type {
  FileMessage,
  FileMessageCreateParams,
  UserMessage,
  UserMessageCreateParams,
} from "@sendbird/chat/message";
import {
  getMessageTypeFromFile,
  type MediaMessageCustomType,
} from "@/lib/api/messages";
import type { AttentionQuote } from "./attention-message";
import {
  SENDBIRD_VOICE_FILENAME,
  SENDBIRD_VOICE_MIME,
  voiceMessageMetaArrays,
} from "./attention-voice-file";

/** Custom types the ops backend keys media messages by, derived from the file. */
export type AttentionFileCustomType = MediaMessageCustomType;

type QuoteReplyParams =
  | { parentMessageId: number; isReplyToChannel: true }
  | Record<string, never>;

function quoteReplyParams(quote: AttentionQuote | null): QuoteReplyParams {
  if (quote === null) return {};
  const parentMessageId = Number(quote.id);
  if (!Number.isFinite(parentMessageId)) return {};
  return { parentMessageId, isReplyToChannel: true };
}

export interface BuildSendTextParamsInput {
  text: string;
  quote: AttentionQuote | null;
  /** Sendbird auto-translation targets, e.g. the teen's preferred language. */
  translationTargetLanguages?: string[] | null;
  /** Custom type for non-plain user messages, e.g. `GIF_MESSAGE`. */
  customType?: string;
}

export function buildSendTextParams({
  text,
  quote,
  translationTargetLanguages,
  customType,
}: BuildSendTextParamsInput): UserMessageCreateParams {
  return {
    message: text,
    ...quoteReplyParams(quote),
    ...(translationTargetLanguages && translationTargetLanguages.length > 0
      ? { translationTargetLanguages }
      : {}),
    ...(customType ? { customType } : {}),
  };
}

export interface BuildSendGifParamsInput {
  url: string;
  quote: AttentionQuote | null;
}

/** GIFs send as a `GIF_MESSAGE` user message carrying the GIF URL as the body. */
export function buildSendGifParams({
  url,
  quote,
}: BuildSendGifParamsInput): UserMessageCreateParams {
  return buildSendTextParams({ text: url, quote, customType: "GIF_MESSAGE" });
}

export interface BuildSendFileParamsInput {
  file: File;
  quote: AttentionQuote | null;
  durationMs?: number;
}

export function buildSendFileParams({
  file,
  quote,
  durationMs,
}: BuildSendFileParamsInput): FileMessageCreateParams {
  const customType = getMessageTypeFromFile(file);
  const isVoice = customType === "VOICE_MESSAGE";
  return {
    file,
    customType,
    ...(isVoice
      ? {
          fileName: SENDBIRD_VOICE_FILENAME,
          mimeType: SENDBIRD_VOICE_MIME,
          ...(durationMs != null
            ? { metaArrays: voiceMessageMetaArrays(durationMs) }
            : {}),
        }
      : {}),
    ...quoteReplyParams(quote),
  };
}

/** Thin, injectable Sendbird senders. Mirror the UIKit message handlers. */
export type AttentionUserMessageSender = ReturnType<
  typeof sendbirdSelectors.getSendUserMessage
>;
export type AttentionFileMessageSender = ReturnType<
  typeof sendbirdSelectors.getSendFileMessage
>;

// Best-effort persistence after a successful send (media needs the blob; text/GIF
// pass `null`). Failures are swallowed so a hiccup never fails a delivered message.
export type PersistAttentionMessage = (
  message: UserMessage | FileMessage,
  file: File | null,
) => Promise<void>;

function persistInBackground(
  persist: PersistAttentionMessage,
  message: UserMessage | FileMessage,
  file: File | null,
): void {
  void Promise.resolve(persist(message, file)).catch((error) => {
    console.error("Attention message persistence failed", error);
  });
}

export interface SendAttentionTextArgs {
  channel: GroupChannel;
  text: string;
  quote: AttentionQuote | null;
  translationTargetLanguages?: string[] | null;
  customType?: string;
  sendUserMessage: AttentionUserMessageSender;
  persist: PersistAttentionMessage;
}

// Resolves once Sendbird confirms the send; rejects only when the send itself fails.
export function sendAttentionText({
  channel,
  text,
  quote,
  translationTargetLanguages,
  customType,
  sendUserMessage,
  persist,
}: SendAttentionTextArgs): Promise<UserMessage> {
  const params = buildSendTextParams({
    text,
    quote,
    translationTargetLanguages,
    customType,
  });

  return new Promise<UserMessage>((resolve, reject) => {
    sendUserMessage(channel, params)
      .onSucceeded((message) => {
        const sent = message as UserMessage;
        persistInBackground(persist, sent, null);
        resolve(sent);
      })
      .onFailed((error) => {
        reject(error);
      });
  });
}

export interface SendAttentionGifArgs {
  channel: GroupChannel;
  url: string;
  quote: AttentionQuote | null;
  sendUserMessage: AttentionUserMessageSender;
  persist: PersistAttentionMessage;
}

export function sendAttentionGif({
  channel,
  url,
  quote,
  sendUserMessage,
  persist,
}: SendAttentionGifArgs): Promise<UserMessage> {
  return sendAttentionText({
    channel,
    text: url,
    quote,
    customType: "GIF_MESSAGE",
    sendUserMessage,
    persist,
  });
}

export interface SendAttentionFileArgs {
  channel: GroupChannel;
  file: File;
  quote: AttentionQuote | null;
  durationMs?: number;
  sendFileMessage: AttentionFileMessageSender;
  persist: PersistAttentionMessage;
}

export function sendAttentionFile({
  channel,
  file,
  quote,
  durationMs,
  sendFileMessage,
  persist,
}: SendAttentionFileArgs): Promise<FileMessage> {
  const params = buildSendFileParams({ file, quote, durationMs });

  return new Promise<FileMessage>((resolve, reject) => {
    sendFileMessage(channel, params)
      .onSucceeded((message) => {
        const sent = message as FileMessage;
        persistInBackground(persist, sent, file);
        resolve(sent);
      })
      .onFailed((error) => {
        reject(error);
      });
  });
}

export function createAttentionUserMessageSender(
  store: Parameters<typeof sendbirdSelectors.getSendUserMessage>[0],
): AttentionUserMessageSender {
  return sendbirdSelectors.getSendUserMessage(store);
}

export function createAttentionFileMessageSender(
  store: Parameters<typeof sendbirdSelectors.getSendFileMessage>[0],
): AttentionFileMessageSender {
  return sendbirdSelectors.getSendFileMessage(store);
}
